import { useState } from "react";
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Fade,
} from "@mui/material";
import {
  ChatBubbleOutline,
  Forum,
  SupportAgent,
  HeadsetMic,
  Message,
  LockReset,
  ArrowBack,
  Email,
  Lock,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#F1F5F9",
  padding: theme.spacing(2),
}));

const AuthCard = styled(Paper)(({ theme }) => ({
  width: "100%",
  maxWidth: 380,
  padding: theme.spacing(3, 3),
  borderRadius: "20px",
  backgroundColor: "#FFFFFF",
  boxShadow: "0 10px 40px -10px rgba(0,0,0,0.08)",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2.5),
    borderRadius: "16px",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#F8FAFC",
    borderRadius: "12px",
    transition: "all 0.2s",
    "& fieldset": {
      borderColor: "#E2E8F0",
    },
    "&:hover fieldset": {
      borderColor: "#CBD5E1",
    },
    "&.Mui-focused": {
      backgroundColor: "#FFFFFF",
      boxShadow: "0 0 0 3px rgba(2, 136, 209, 0.1)",
      "& fieldset": {
        borderColor: "#0288D1",
        borderWidth: "1.5px",
      },
    },
  },
  "& .MuiInputLabel-root": {
    color: "#64748B",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#0288D1",
    fontWeight: 600,
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: "12px",
  fontSize: "1rem",
  fontWeight: 600,
  textTransform: "none",
  color: "#fff",
  background: "linear-gradient(135deg, #29B6F6 0%, #0288D1 100%)",
  boxShadow: "0 4px 12px rgba(2, 136, 209, 0.3)",
  transition: "all 0.2s",
  marginTop: theme.spacing(1),
  "&:hover": {
    background: "linear-gradient(135deg, #039BE5 0%, #01579B 100%)",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(2, 136, 209, 0.4)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
  "&.Mui-disabled": {
    background: "#E2E8F0",
    color: "#94A3B8",
    boxShadow: "none",
  },
}));

const PasswordTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#F8FAFC",
    borderRadius: "12px",
    transition: "all 0.2s",
    "& fieldset": {
      borderColor: "#E2E8F0",
    },
    "&:hover fieldset": {
      borderColor: "#CBD5E1",
    },
    "&.Mui-focused": {
      backgroundColor: "#FFFFFF",
      boxShadow: "0 0 0 3px rgba(2, 136, 209, 0.1)",
      "& fieldset": {
        borderColor: "#0288D1",
        borderWidth: "1.5px",
      },
    },
  },
  "& .MuiInputLabel-root": {
    color: "#64748B",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#0288D1",
    fontWeight: 600,
  },
}));

const BackgroundIcon = styled(Box)(({ theme, top, left, right, bottom, size, rotation, delay }) => ({
  position: "absolute",
  top: top,
  left: left,
  right: right,
  bottom: bottom,
  fontSize: size,
  color: "#0288D1",
  opacity: 0.04,
  transform: `rotate(${rotation || 0}deg)`,
  pointerEvents: "none",
  zIndex: 0,
  filter: "blur(1px)",
  animation: "float 10s ease-in-out infinite",
  animationDelay: delay || "0s",
  "@keyframes float": {
    "0%, 100%": { transform: `translateY(0) rotate(${rotation || 0}deg)` },
    "50%": { transform: `translateY(-20px) rotate(${rotation || 0}deg)` },
  },
}));

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [step, setStep] = useState(1); // 1: Enter Email, 2: Reset Password

  // Reset Password States
  const [tempPassword, setTempPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Handle email submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    try {
      // Simulate API call to send temp password
      await new Promise(resolve => setTimeout(resolve, 1000));

      console.log("Temp password sent to:", email);

      setSuccess(`Temporary password sent to ${email}. Check your inbox.`);
      setStep(2); // Move to password reset step
      setLoading(false);
    } catch (err) {
      setError("Failed to send temporary password. Please try again.");
      setLoading(false);
    }
  };

  // Handle password reset
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError("");

    // Validation
    if (!tempPassword.trim()) {
      setResetError("Please enter the temporary password from your email");
      return;
    }

    if (!newPassword.trim()) {
      setResetError("Please enter a new password");
      return;
    }

    if (newPassword.length < 6) {
      setResetError("Password must be at least 6 characters long");
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetError("Passwords do not match");
      return;
    }

    setResetLoading(true);

    try {
      // Simulate API call to reset password
      await new Promise(resolve => setTimeout(resolve, 1000));

      console.log("Password reset for:", email);
      setSuccess("Password reset successfully! Redirecting to login...");

      // Redirect to login after a short delay
      setTimeout(() => {
        navigate("/");
      }, 1500);
    } catch (err) {
      setResetError("Failed to reset password. Please try again.");
      setResetLoading(false);
    }
  };

  // Handle go back from password reset
  const handleGoBack = () => {
    setStep(1);
    setTempPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setResetError("");
    setSuccess("");
  };

  return (
    <>
      <PageWrapper>
        {/* Subtle Background Icons */}
        <BackgroundIcon top="10%" left="5%" size={120} rotation={-15} delay="0s">
          <ChatBubbleOutline fontSize="inherit" />
        </BackgroundIcon>

        <BackgroundIcon top="15%" right="10%" size={100} rotation={15} delay="2s">
          <SupportAgent fontSize="inherit" />
        </BackgroundIcon>

        <BackgroundIcon bottom="10%" left="10%" size={140} rotation={-10} delay="1s">
          <Forum fontSize="inherit" />
        </BackgroundIcon>

        <BackgroundIcon bottom="20%" right="5%" size={90} rotation={20} delay="3s">
          <HeadsetMic fontSize="inherit" />
        </BackgroundIcon>

        <BackgroundIcon top="40%" left="20%" size={60} rotation={45} delay="4s">
          <Message fontSize="inherit" />
        </BackgroundIcon>

        <Fade in={true} timeout={600}>
          <AuthCard elevation={0} sx={{ position: "relative", zIndex: 1 }}>
            {/* Step 1: Enter Email */}
            {step === 1 && (
              <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box textAlign="center" mb={0.5}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: "#E0F2FE",
                      color: "#0288D1",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto",
                      mb: 1.5
                    }}
                  >
                    <LockReset sx={{ fontSize: 26 }} />
                  </Box>
                  <Typography variant="h5" fontWeight={700} color="#1E293B" sx={{ mb: 0.5, letterSpacing: '-0.5px' }}>
                    Forgot Password?
                  </Typography>
                  <Typography variant="body2" color="#64748B" fontSize="0.875rem">
                    Enter your email to receive a temporary password
                  </Typography>
                </Box>

                {error && (
                  <Alert severity="error" sx={{ borderRadius: "12px" }}>{error}</Alert>
                )}

                {success && (
                  <Alert severity="success" sx={{ borderRadius: "12px" }}>{success}</Alert>
                )}

                <StyledTextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: "#94A3B8" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ display: "flex", flexDirection: 'column', gap: 1.5, mt: 1 }}>
                  <GradientButton
                    fullWidth
                    type="submit"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {loading ? "Sending..." : "Send Temporary Password"}
                  </GradientButton>

                  <Button
                    fullWidth
                    onClick={() => navigate("/")}
                    startIcon={<ArrowBack fontSize="small" />}
                    sx={{
                      color: "#64748B",
                      textTransform: "none",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      "&:hover": { bgcolor: "#F1F5F9", color: "#1E293B" }
                    }}
                  >
                    Back to Login
                  </Button>
                </Box>
              </Box>
            )}

            {/* Step 2: Reset Password */}
            {step === 2 && (
              <Box component="form" onSubmit={handleResetPassword} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box textAlign="center" mb={0.5}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: "#E0F2FE",
                      color: "#0288D1",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto",
                      mb: 1.5
                    }}
                  >
                    <Lock sx={{ fontSize: 26 }} />
                  </Box>
                  <Typography variant="h5" fontWeight={700} color="#1E293B" sx={{ mb: 0.5, letterSpacing: '-0.5px' }}>
                    Reset Password
                  </Typography>
                  <Typography variant="body2" color="#64748B" fontSize="0.875rem">
                    Enter the temporary password and create a new one
                  </Typography>
                </Box>

                {resetError && (
                  <Alert severity="error" sx={{ borderRadius: "12px" }}>{resetError}</Alert>
                )}

                {success && (
                  <Alert severity="success" sx={{ borderRadius: "12px" }}>{success}</Alert>
                )}

                <PasswordTextField
                  fullWidth
                  label="Temporary Password"
                  type="password"
                  value={tempPassword}
                  onChange={(e) => setTempPassword(e.target.value)}
                  required
                  disabled={resetLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: "#94A3B8" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <PasswordTextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={resetLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: "#94A3B8" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff sx={{ color: "#94A3B8" }} /> : <Visibility sx={{ color: "#94A3B8" }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <PasswordTextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={resetLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: "#94A3B8" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? <VisibilityOff sx={{ color: "#94A3B8" }} /> : <Visibility sx={{ color: "#94A3B8" }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ display: "flex", flexDirection: 'column', gap: 1.5, mt: 1 }}>
                  <GradientButton
                    fullWidth
                    type="submit"
                    disabled={resetLoading}
                    startIcon={resetLoading ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {resetLoading ? "Resetting..." : "Reset Password"}
                  </GradientButton>

                  <Button
                    fullWidth
                    onClick={handleGoBack}
                    startIcon={<ArrowBack fontSize="small" />}
                    disabled={resetLoading}
                    sx={{
                      color: "#64748B",
                      textTransform: "none",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      "&:hover": { bgcolor: "#F1F5F9", color: "#1E293B" }
                    }}
                  >
                    Back to Email
                  </Button>
                </Box>
              </Box>
            )}
          </AuthCard>
        </Fade>
      </PageWrapper>
    </>
  );
}